import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from '@auth0/auth0-react';

import ApiMethods from "./ApiMethods";

export const useMoveDatasetToProject = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useMutation({
        mutationFn: async ({ datasetId, destProjectId }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.put(
                `v1/sequencing_runs/${datasetId}`,
                accessToken,
                { data: {
                    type: "sequencing_run",
                    id: datasetId,
                    attributes: { project_id: destProjectId }
                }}
            );
        },
    });
}

export const useMoveSampleToDataset = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useMutation({
        mutationFn: async ({ sampleId, destDatasetId }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.put(
                `v1/lib_seqs/${sampleId}`,
                accessToken,
                { data: {
                    type: "lib_seq",
                    id: sampleId,
                    attributes: { run_id: destDatasetId }
                }}
            );
        },
    });
}

export const useUpdateDatasetName = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useMutation({
        mutationFn: async ({ datasetId, newDatasetName }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.put(
                `v1/sequencing_runs/${datasetId}`,
                accessToken,
                { data: {
                    type: "sequencing_run",
                    id: datasetId,
                    attributes: { name: newDatasetName }
                }}
            );
        },
    });
}

export const useCreateSample = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useMutation({
        mutationFn: async ({ runId, sampleType, sampleName, file }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.post(
                `v1/sequencing_runs/${runId}/lib_seqs`,
                accessToken,
                { data: {
                    type: "lib_seq",
                    attributes: {
                        sample_type: sampleType,
                        sample_name: sampleName,
                        sample_id: sampleName,
                        library_id: file?.lastModified || Date.now(),
                    }
                }}
            );
        },
    });
}

export const useUpdateSample = () => {
    const { getAccessTokenSilently } = useAuth0();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ sampleId, sampleType }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.put(
                `v1/lib_seqs/${sampleId}`,
                accessToken,
                { data: {
                    type: "lib_seq",
                    attributes: {
                        sample_type: sampleType,
                    }
                }}
            );
        },
        onSuccess: (sample) => {
            // Invalidate queries related to the sample to refresh data
            return queryClient.invalidateQueries({
                predicate: ({ queryKey }) => queryKey[0].startsWith(`v1/lib_seqs/${sample.id}`) || queryKey[0].startsWith(`v1/lib_seqs/${sample.attributes.uuid}`)
            });
        },
    });
}

export const useCreateConcatenatedFastqgz = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useMutation({
        mutationFn: async ({ libSeqId, queryParams }) => {
            const accessToken = await getAccessTokenSilently();
            const { extraFields } = queryParams;

            let url = `v1/lib_seqs/${libSeqId}/concatenated_fastqgzs`;
            if (extraFields) url += `?extra_fields=${extraFields.join(",")}`;

            return ApiMethods.post(
                url,
                accessToken,
                { data: {
                    type: "concatenated_fastqgz",
                    attributes: {}
                }}
            );
        },
    });
}

export const useUpdateUser = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useMutation({
        mutationFn: async ({ userId, attributes }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.put(
                `v1/users/${userId}`,
                accessToken,
                { data: {
                    type: "user",
                    id: userId,
                    attributes: attributes
                }}
            );
        },
    });
}

export const useCreateDataset = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useMutation({
        mutationFn: async ({ projectId, attributes }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.post(
                `v1/projects/${projectId}/sequencing_runs`,
                accessToken,
                { data: {
                    type: "sequencing_run",
                    attributes: attributes
                }}
            );
        },
    });
}

export const useUpdateDataset = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useMutation({
        mutationFn: async ({ datasetId, attributes }) => {
            const accessToken = await getAccessTokenSilently();
            return ApiMethods.put(
                `v1/sequencing_runs/${datasetId}`,
                accessToken,
                { data: {
                    type: "sequencing_run",
                    id: datasetId,
                    attributes: attributes
                }}
            );
        },
    });
}

export const useSummarizeAnalysis = () => {
    const { getAccessTokenSilently } = useAuth0();

    return useMutation({
        mutationFn: async ({ analysisId }) => {
            const accessToken = await getAccessTokenSilently();
            console.log("Summarizing analysis with ID:", analysisId);
            return ApiMethods.put(
                `v1/analyses/${analysisId}/summarize`,
                accessToken,
                {}
            );
        },
    })
}

export const useAnalyzeClonality = ({ onSuccessFn, onErrorFn }) => {
  const { getAccessTokenSilently } = useAuth0();

  return useMutation({
    mutationFn: async ({ runId }) => {
      const accessToken = await getAccessTokenSilently();
      return ApiMethods.put(
        `v1/sequencing_runs/${runId}/analyze_clonality`,
        accessToken,
        {}
      );
    },
    onSuccess: onSuccessFn,
    onError: onErrorFn,
  });
}
